import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Beef, Store, ChefHat, Loader2, ShieldCheck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin(intentRole = null) {
  if (intentRole) localStorage.setItem("cw_google_intent_role", intentRole);
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const maskCPF = (v) => v.replace(/\D/g, "").slice(0, 11)
  .replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
const maskCNPJ = (v) => v.replace(/\D/g, "").slice(0, 14)
  .replace(/^(\d{2})(\d)/, "$1.$2").replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
  .replace(/\.(\d{3})(\d)/, ".$1/$2").replace(/(\d{4})(\d)/, "$1-$2");

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(params.get("role") ? "form" : "role");
  const [role, setRole] = useState(params.get("role") || "");
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", city: "",
    business_name: "", specialty: "Açougueiro", document: "",
    latitude: -23.5505, longitude: -46.6333,
    accepted_terms: false,
  });
  const [loading, setLoading] = useState(false);
  const setField = (k, v) => setForm(s => ({ ...s, [k]: v }));

  const pickRole = (r) => { setRole(r); setStep("form"); setField("document", ""); };
  const setDocument = (v) => setField("document", role === "comercio" ? maskCNPJ(v) : maskCPF(v));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accepted_terms) {
      toast.error("É preciso aceitar os Termos de Uso e Política de Privacidade");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, role, document: form.document.replace(/\D/g, "") });
      toast.success("Cadastro concluído!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grain flex items-center justify-center px-4 py-10 relative z-10">
      <div className="w-full max-w-lg">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center">
            <Beef className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-xl">CarnesWork</span>
        </Link>

        <div className="cw-card p-8 anim-fade-up">
          {step === "role" ? (
            <>
              <h1 className="text-2xl font-display font-bold mb-1">Escolha seu perfil</h1>
              <p className="text-sm text-zinc-400 mb-6">Como você vai usar a plataforma?</p>
              <div className="grid gap-3">
                <button onClick={() => pickRole("comercio")} data-testid="btn-role-comercio"
                        className="text-left p-5 rounded-xl border border-zinc-800 hover:border-red-600 transition-all">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-11 h-11 rounded-lg bg-red-500/15 flex items-center justify-center">
                      <Store className="w-5 h-5 text-red-500" />
                    </div>
                    <div className="font-semibold text-lg">Sou Comércio</div>
                  </div>
                  <div className="text-sm text-zinc-400">Açougue, casa de carnes ou supermercado. Publique diárias e contrate profissionais.</div>
                </button>
                <button onClick={() => pickRole("acougueiro")} data-testid="btn-role-acougueiro"
                        className="text-left p-5 rounded-xl border border-zinc-800 hover:border-red-600 transition-all">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-11 h-11 rounded-lg bg-amber-500/15 flex items-center justify-center">
                      <ChefHat className="w-5 h-5 text-amber-500" />
                    </div>
                    <div className="font-semibold text-lg">Sou Profissional</div>
                  </div>
                  <div className="text-sm text-zinc-400">Açougueiro ou balconista. Encontre diárias, negocie valores e ganhe reputação.</div>
                </button>
              </div>

              <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-widest text-zinc-500">
                <div className="h-px flex-1 bg-zinc-800" />
                ou
                <div className="h-px flex-1 bg-zinc-800" />
              </div>
              <button onClick={() => startGoogleLogin()} type="button"
                      className="w-full flex items-center justify-center gap-3 rounded-xl border border-zinc-700 hover:border-zinc-500 bg-white text-zinc-900 font-semibold py-2.5 transition-colors"
                      data-testid="btn-google-register">
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.3 30.2 0 24 0 14.6 0 6.4 5.4 2.6 13.2l7.8 6C12.2 13 17.6 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.7c-.6 3-2.3 5.5-4.9 7.2l7.5 5.8c4.4-4.1 7.2-10.1 7.2-17.5z"/>
                  <path fill="#FBBC05" d="M10.4 28.9a14.4 14.4 0 0 1 0-9.8l-7.8-6a24 24 0 0 0 0 21.6l7.8-5.8z"/>
                  <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2.1 1.4-4.8 2.2-8.4 2.2-6.4 0-11.8-3.5-13.6-9.4l-7.8 5.8C6.4 42.6 14.6 48 24 48z"/>
                </svg>
                Cadastrar com Google
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep("role")} className="text-xs text-zinc-500 mb-2 hover:text-white">← Alterar perfil</button>
              <h1 className="text-2xl font-display font-bold mb-1">
                {role === "comercio" ? "Cadastro do Comércio" : "Cadastro do Profissional"}
              </h1>
              <p className="text-sm text-zinc-400 mb-6">Preencha os dados abaixo</p>

              <form onSubmit={submit} className="space-y-3">
                {role === "comercio" && (
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-500">Nome do estabelecimento</label>
                    <input required className="cw-input mt-1" value={form.business_name} data-testid="input-business-name"
                           onChange={e => setField("business_name", e.target.value)} placeholder="Açougue do Zé" />
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500">
                    {role === "comercio" ? "Responsável" : "Nome completo"}
                  </label>
                  <input required className="cw-input mt-1" value={form.name} data-testid="input-name"
                         onChange={e => setField("name", e.target.value)} placeholder="Seu nome" />
                </div>

                <div>
                  <label className="text-xs uppercase tracking-wider text-zinc-500">
                    {role === "comercio" ? "CNPJ" : "CPF"}
                  </label>
                  <input required className="cw-input mt-1" value={form.document} data-testid="input-document"
                         onChange={e => setDocument(e.target.value)}
                         placeholder={role === "comercio" ? "00.000.000/0000-00" : "000.000.000-00"} />
                  <div className="text-[10px] text-zinc-500 mt-1">
                    <ShieldCheck className="w-3 h-3 inline mr-1" />
                    Usado apenas para validação. Nunca compartilhamos com terceiros.
                  </div>
                </div>

                {role === "acougueiro" && (
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-500">Especialidade</label>
                    <select className="cw-input mt-1" value={form.specialty} data-testid="select-specialty"
                            onChange={e => setField("specialty", e.target.value)}>
                      <option>Açougueiro</option>
                      <option>Balconista</option>
                      <option>Desossador</option>
                      <option>Auxiliar</option>
                    </select>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-500">Email</label>
                    <input required type="email" className="cw-input mt-1" value={form.email} data-testid="input-register-email"
                           onChange={e => setField("email", e.target.value)} placeholder="voce@email.com" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-500">Senha</label>
                    <input required type="password" className="cw-input mt-1" value={form.password} data-testid="input-register-password"
                           onChange={e => setField("password", e.target.value)} placeholder="min. 6 caracteres" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-500">Telefone</label>
                    <input className="cw-input mt-1" value={form.phone} data-testid="input-phone"
                           onChange={e => setField("phone", e.target.value)} placeholder="(11) 99999-9999" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-zinc-500">Cidade</label>
                    <input className="cw-input mt-1" value={form.city} data-testid="input-city"
                           onChange={e => setField("city", e.target.value)} placeholder="São Paulo" />
                  </div>
                </div>

                <label className="flex items-start gap-2 text-xs text-zinc-400 pt-2 cursor-pointer">
                  <input type="checkbox" checked={form.accepted_terms} data-testid="checkbox-terms"
                         onChange={e => setField("accepted_terms", e.target.checked)}
                         className="mt-0.5 accent-red-600 w-4 h-4" />
                  <span>
                    Li e aceito os <Link to="/terms" target="_blank" className="text-red-500 underline">Termos de Uso</Link>
                    {" "}e a <Link to="/privacy" target="_blank" className="text-red-500 underline">Política de Privacidade</Link> (LGPD).
                  </span>
                </label>

                <button type="submit" disabled={loading} className="cw-btn-primary w-full flex items-center justify-center gap-2 mt-2" data-testid="btn-register-submit">
                  {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Criando conta…</> : "Criar conta"}
                </button>
              </form>
            </>
          )}

          <p className="text-sm text-zinc-400 text-center mt-6">
            Já tem conta? <Link to="/login" className="text-red-500 font-semibold" data-testid="link-goto-login">Entre</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
